import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { step1State, step3State, step4State, STEP2_STATE } from '@/lib/setupSteps';
import VideoEmbed from './onboarding/VideoEmbed';
import SetupStep from './onboarding/SetupStep';
import SupportFooter from './onboarding/SupportFooter';
import CollapsibleSetupStep from './onboarding/CollapsibleSetupStep';
import { hubspotAppCardsUrl } from '../utils/externalUrls';

const HERO_VIDEO_ID = '4CBgAsjtURg';
const INSTALL_VIDEO_ID = 'B1jZC-f61a4';

function ConfigureLink() {
  return (
    <Link
      to="/settings"
      className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
    >
      Configure <ArrowUpRight className="h-3.5 w-3.5" />
    </Link>
  );
}

const SetupGuide: React.FC = () => {
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const hubspot = useQuery({
    queryKey: ['setup-hubspot-status', tenantId],
    queryFn: () => apiClient.getHubspotOauthStatus(),
    enabled: !!tenantId,
  });
  const stripe = useQuery({
    queryKey: ['setup-stripe-status', tenantId],
    queryFn: () => apiClient.getStripeOauthStatus(),
    enabled: !!tenantId,
  });
  const settings = useQuery({
    queryKey: ['setup-tenant-settings', tenantId],
    queryFn: () => apiClient.getTenantSettings(),
    enabled: !!tenantId,
  });
  const deals = useQuery({
    queryKey: ['setup-deals', tenantId],
    queryFn: () => apiClient.listDeals(100),
    enabled: !!tenantId,
  });

  const state1 = step1State({
    hubspot: hubspot.data,
    stripe: stripe.data,
    loading: hubspot.isLoading || stripe.isLoading,
    error: hubspot.isError || stripe.isError,
  });
  const state3 = step3State({
    settings: settings.data,
    loading: settings.isLoading,
    error: settings.isError,
  });
  const state4 = step4State({
    deals: deals.data?.deals,
    loading: deals.isLoading,
    error: deals.isError,
  });

  const portalId = settings.data?.hubspot_portal_id;
  const appCardsUrl = portalId ? hubspotAppCardsUrl(portalId) : undefined;

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold">Setup Guide</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Get Finrite syncing your HubSpot deals into Stripe.
        </p>
      </div>

      <Card>
        <CardContent className="p-4">
          <VideoEmbed id={HERO_VIDEO_ID} title="Finrite — Setup guide" />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>How to Setup</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <SetupStep
            index={1}
            state={state1}
            title="Connect Stripe & HubSpot"
            description="Authorize Finrite so it can read your deals, contacts, and product library, and create subscriptions in Stripe."
          >
            <div>
              <ConfigureLink />
            </div>
          </SetupStep>

          <CollapsibleSetupStep
            index={2}
            state={STEP2_STATE}
            title="Install the Stripe & HubSpot apps"
            description="Install the Finrite apps in your Stripe and HubSpot accounts. Click to watch the install walkthrough."
          >
            <VideoEmbed id={INSTALL_VIDEO_ID} title="Installing the Stripe and HubSpot apps" />
            <div className="flex flex-wrap gap-4">
              <a
                href="https://ecosystem.hubspot.com/marketplace/listing/finrite"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                HubSpot listing <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
              <a
                href="https://marketplace.stripe.com/apps/finrite-hubspot-subscription-sync"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Stripe app <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </CollapsibleSetupStep>

          <SetupStep
            index={3}
            state={STEP2_STATE}
            title="Enable the Finrite card in HubSpot"
            description="Add the Finrite card to your HubSpot deal records so you can view Stripe billing and trigger syncs from the CRM."
          >
            {appCardsUrl ? (
              <a
                href={appCardsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
              >
                Open in HubSpot <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            ) : null}
          </SetupStep>

          <SetupStep
            index={4}
            state={state3}
            title="Turn on billing sync"
            description="Choose which deal stages trigger Stripe subscriptions and enable automatic sync on deal close."
          >
            <ConfigureLink />
          </SetupStep>

          <SetupStep
            index={5}
            state={state4}
            title="Check your first synced deals"
            description="Confirm deals are flowing into Stripe as subscriptions, with customers matched correctly."
          >
            <Link
              to="/"
              className="inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline"
            >
              View deals <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </SetupStep>
        </CardContent>
      </Card>

      <SupportFooter />
    </div>
  );
};

export default SetupGuide;
